import React, { useCallback, useEffect, useState } from 'react';

import { Select, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getMonitoringData } from 'client/api';
import { AliceAppMonitoringDatePicker } from 'client/blocks-redesign/AliceAppMonitoringDatePicker/AliceAppMonitoringDatepicker';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { useLoadingData } from 'client/hooks/useLoadingData';
import { usePushHistory } from 'client/hooks/usePushHistory';
import { textToKey } from 'client/lib/i18n-utils';
import { moment } from 'client/lib/moment';
import { Channel, Skill } from 'client/model/skill';
import { SolomonLabels } from 'client/model/solomon';
import { getDayRange } from 'client/utils/monitoring';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as keyset from 'i18n/Monitoring';

import { AliceAppMonitoringPageHistoryControls } from '../AliceAppMonitoring/AliceAppMonitoringPageHistoryControls';
import { AliceAppMonitoringSection } from '../AliceAppMonitoring/AliceAppMonitoringSection';

import styles from './AliceAppMonitoringDashboard.module.scss';

const t = i18n(keyset);
const tGeneral = i18n(generalSettingsKeyset);

type I18NLiteral = keyof (typeof keyset)[keyof typeof keyset];

interface Props {
    skill: Skill;
    solomonLabels: SolomonLabels;
}

export const AliceAppMonitoringDashboard: React.FC<Props> = ({ skill, solomonLabels }) => {
    const channel = skill.channel as Channel.AliceSkill | Channel.SmartHome;
    const commands = solomonLabels.commands[channel];
    const sources = solomonLabels.sources[channel];

    const [source, setSource] = useState(sources[0].value);
    const [command, setCommand] = useState(commands[0].value);

    const { add: toasterAdd } = useToaster();

    const presentDayRange = getDayRange();

    const {
        currentValue: currentRange,
        goBack: goPrevRange,
        goForward: goNextRange,
        init: initRanges,
        push: pushRange,
        hasNext: hasNextRange,
        hasPrev: hasPrevRange,
        values: timeRanges,
    } = usePushHistory([presentDayRange]);

    const from = currentRange?.from || presentDayRange.from;
    const to = currentRange?.to || presentDayRange.to;

    const loader = useCallback(() => {
        return getMonitoringData(skill.id, {
            channel,
            command,
            source,
            from,
            to,
        }).catch((_err) => {
            toasterAdd({ title: tGeneral('error'), theme: 'danger', name: 'MONITORING_TOAST', autoHiding: false });
        });
    }, [skill.id, channel, command, source, from, to, toasterAdd]);

    const { loadingMeta, reload } = useLoadingData({ loader });

    const { state, data: chartRowsMeta } = loadingMeta;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(reload, [command, source, from, to]);

    const onIntervalChange = (startTs: number, endTs: number) => {
        const newFrom = Math.min(endTs, startTs);
        const newTo = Math.max(endTs, startTs);

        if (newTo - newFrom < __appConfig.monitoring.minChartInterval) {
            return;
        }

        pushRange({ from: newFrom, to: newTo });
    };

    const displayHistoryControls = hasNextRange || hasPrevRange;
    const isSettingsDisabled = state === 'loading';

    if (!chartRowsMeta) {
        return <PageLoader />;
    }

    return (
        <>
            <div className={styles.monitoringDashboard__settings}>
                <div className={styles.monitoringDashboard__settingsControls}>
                    <AliceAppMonitoringDatePicker
                        disabled={isSettingsDisabled}
                        from={moment(from)}
                        to={moment(to)}
                        onChange={(from, to) => {
                            const fromTs = from.valueOf();
                            const toTs = to.valueOf();

                            if (toTs - fromTs < __appConfig.monitoring.minChartInterval) {
                                return;
                            }

                            initRanges([{ from: fromTs, to: toTs }]);
                        }}
                    />
                    <div className={styles.monitoringDashboard__options}>
                        <div className={styles.monitoringDashboard__optionLabel}>{t('option-source')}</div>
                        <Select
                            value={[source]}
                            onUpdate={(values) => setSource(values[0])}
                            options={sources.map(({ text, value }) => ({
                                value: value,
                                content: t(textToKey(text) as I18NLiteral),
                            }))}
                            size="l"
                            disabled={isSettingsDisabled}
                        />

                        <div className={styles.monitoringDashboard__optionLabel}>{t('option-request-type')}</div>
                        <Select
                            value={[command]}
                            onUpdate={(values) => setCommand(values[0])}
                            options={commands.map(({ text, value }) => ({
                                value: value,
                                content: t(textToKey(text) as I18NLiteral),
                            }))}
                            size="l"
                            disabled={isSettingsDisabled}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.monitoringDashboard__section}>
                {chartRowsMeta!.map(({ text, charts }, i) => {
                    return (
                        <div key={i}>
                            <div className={styles.monitoringDashboard__rowHeader}>
                                <h2 className={styles.monitoringDashboard__rowTitle}>
                                    {t(textToKey(text) as I18NLiteral)}
                                </h2>
                                {displayHistoryControls && (
                                    <AliceAppMonitoringPageHistoryControls
                                        goNextRange={goNextRange}
                                        goPrevRange={goPrevRange}
                                        hasNextRange={hasNextRange}
                                        hasPrevRange={hasPrevRange}
                                        initRanges={() => initRanges(timeRanges.slice(0, 1))}
                                    />
                                )}
                            </div>
                            <div className={styles.monitoringDashboard__section}>
                                <AliceAppMonitoringSection
                                    charts={charts}
                                    onIntervalChange={onIntervalChange}
                                    dataLoadingState={state}
                                    from={from}
                                    to={to}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};
